// components/forms/OrganizationRegistrationForm.jsx
"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeOff, Upload } from "lucide-react";
import { organizationSchema } from "@/lib/validation-schemas";

export function OrganizationRegistrationForm({ onSubmit, isSubmitting, isInternational = false }) {
  const [showPassword, setShowPassword] = useState(false);
  const [profileDocument, setProfileDocument] = useState(null);

  const form = useForm({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
          userType: "organization",   // 👈 add this
      organizationName: "",
      email: "",
      password: "",
      position: "",
      ceoName: "",
      phone: "",
      country: "",
      city: "",
      abbreviation: "",
      otherEmail: "",
      description: "",
      website: "",
      foundingYear: "",
      staffSize: "",
      sectors: [],
      countriesOfOperation: [],
      termsAccepted: false,
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file
      const validTypes = ["application/pdf"];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        form.setError("profileDocument", { message: "Please upload a PDF file" });
        return;
      }
      
      if (file.size > maxSize) {
        form.setError("profileDocument", { message: "File size should be less than 10MB" });
        return;
      }
      
      setProfileDocument(file);
      form.setValue("profileDocument", file);
      form.clearErrors("profileDocument");
    }
  };

  const handleSubmit = async (data) => {
    if (profileDocument) {
      data.profileDocument = profileDocument;
    }
    await onSubmit(data);
  };

  // Countries for multi-select (you can fetch this from API)
  const countries = [
    { value: "ye", label: "Yemen" },
    { value: "sa", label: "Saudi Arabia" },
    { value: "ae", label: "United Arab Emirates" },
    { value: "eg", label: "Egypt" },
    { value: "us", label: "United States" },
    { value: "gb", label: "United Kingdom" },
    { value: "fr", label: "France" },
    { value: "de", label: "Germany" },
  ];

  // Sectors for multi-select
  const sectors = [
    { value: "humanitarian", label: "Humanitarian Aid" },
    { value: "health", label: "Health & Nutrition" },
    { value: "education", label: "Education" },
    { value: "protection", label: "Protection" },
    { value: "wash", label: "WASH (Water, Sanitation & Hygiene)" },
    { value: "food", label: "Food Security" },
    { value: "livelihoods", label: "Livelihoods & Economic Recovery" },
    { value: "shelter", label: "Shelter & NFIs" },
    { value: "peace", label: "Peacebuilding" },
    { value: "gender", label: "Gender Equality" },
    { value: "child", label: "Child Protection" },
    { value: "environment", label: "Environment & Climate" },
    { value: "disability", label: "Disability Inclusion" },
  ];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold">
                  {isInternational ? "International Organization Registration" : "NGO Registration"}
                </h2>
                <p className="text-muted-foreground">
                  Register your organization to access the Mahlia Network platform
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Organization Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="organizationName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization Name *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Organization name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="abbreviation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Abbreviation/Acronym</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., UN, WHO, MSF" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Official Email *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="info@organization.org" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="otherEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alternative Email</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" placeholder="alternative@email.org" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://organization.org" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="foundingYear"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Year Founded</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" min="1900" max="2024" placeholder="e.g., 2010" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="staffSize"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Staff Size</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select size" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="1-10">1-10 employees</SelectItem>
                            <SelectItem value="11-50">11-50 employees</SelectItem>
                            <SelectItem value="51-200">51-200 employees</SelectItem>
                            <SelectItem value="201-500">201-500 employees</SelectItem>
                            <SelectItem value="501-1000">501-1,000 employees</SelectItem>
                            <SelectItem value="1000+">1,000+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Headquarters Country *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {countries.map(country => (
                              <SelectItem key={country.value} value={country.value}>
                                {country.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Headquarters City *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="City" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Brief description of your organization's mission and work..."
                          className="min-h-[100px]"
                        />
                      </FormControl>
                      <FormDescription>
                        This will be displayed on your organization profile
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-6 pt-6 border-t">
                <h3 className="text-lg font-semibold">Contact Person Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="ceoName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{isInternational ? "Director/Head Name" : "CEO/Executive Director Name"}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Full name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="position"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your Position *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., Program Manager, Director" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Phone *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="+1234567890" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-6 pt-6 border-t">
                <h3 className="text-lg font-semibold">Account Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Password *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type={showPassword ? "text" : "password"}
                              placeholder="Create a strong password"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                              onClick={togglePasswordVisibility}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <Eye className="h-4 w-4 text-muted-foreground" />
                              )}
                            </Button>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Minimum 8 characters with uppercase, lowercase, and numbers
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password *</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="Confirm your password"
                            onChange={(e) => {
                              const confirmPassword = e.target.value;
                              if (confirmPassword !== field.value) {
                                form.setError("password", {
                                  type: "manual",
                                  message: "Passwords do not match",
                                });
                              } else {
                                form.clearErrors("password");
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="space-y-6 pt-6 border-t">
                <h3 className="text-lg font-semibold">Additional Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FormLabel>Sectors of Work</FormLabel>
                    <FormDescription className="mb-2">
                      Select all sectors your organization works in
                    </FormDescription>
                    <div className="grid grid-cols-2 gap-2">
                      {sectors.map((sector) => (
                        <FormField
                          key={sector.value}
                          control={form.control}
                          name="sectors"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={sector.value}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(sector.value)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value, sector.value])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== sector.value
                                            )
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {sector.label}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <FormLabel>Countries of Operation</FormLabel>
                    <FormDescription className="mb-2">
                      Select countries where your organization operates
                    </FormDescription>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {countries.map((country) => (
                        <FormField
                          key={country.value}
                          control={form.control}
                          name="countriesOfOperation"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={country.value}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(country.value)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([...field.value, country.value])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== country.value
                                            )
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {country.label}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t">
                <FormLabel>Organization Profile Document</FormLabel>
                <FormDescription>
                  Upload your organization's profile, registration certificate, or annual report (PDF, max 10MB)
                </FormDescription>
                
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <Upload className="h-12 w-12 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Upload document</p>
                      <p className="text-xs text-muted-foreground">
                        PDF files only, maximum size 10MB
                      </p>
                    </div>
                    <div>
                      <Input
                        id="profileDocument"
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('profileDocument').click()}
                      >
                        Choose File
                      </Button>
                    </div>
                  </div>
                  {profileDocument && (
                    <div className="mt-4 p-3 bg-muted rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            <span className="text-primary font-bold">PDF</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{profileDocument.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {(profileDocument.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setProfileDocument(null);
                            form.setValue("profileDocument", null);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <FormMessage />
              </div>

              <FormField
                control={form.control}
                name="termsAccepted"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        I confirm that I am an authorized representative of this organization
                      </FormLabel>
                      <FormDescription>
                        By registering, you agree to our{" "}
                        <a href="/terms" className="text-primary hover:underline">
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a href="/privacy" className="text-primary hover:underline">
                          Privacy Policy
                        </a>
                      </FormDescription>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Registering Organization...
                    </>
                  ) : (
                    `Register ${isInternational ? 'International Organization' : 'NGO'}`
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}